// All languages the site has translations for. Bulgarian and Italian are kept but switched off for now
export const ALL_LANGUAGES = ['en', 'bg', 'it'] as const;
export type SupportedLanguage = (typeof ALL_LANGUAGES)[number];
// Languages offered on the site (UK / English only). Other locale URLs redirect to /en
export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['en'];
import { italianShopIds } from './constants';
export interface LanguageConfig {
  code: SupportedLanguage;
  name: string;
  country: string;
  locale: string;
  timezone: string;
  currency: {
    symbol: string;
    code: string;
    key: string; // Translation key for currency
  };
  flag: string;
  dateFormat: string;
}

export const LANGUAGE_CONFIGS: Record<SupportedLanguage, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    country: 'UK',
    locale: 'en-GB',
    timezone: 'europe_london',
    currency: { symbol: '£', code: 'GBP', key: '' },
    flag: 'gb',
    dateFormat: 'DD/MM/YYYY',
  },
  bg: {
    code: 'bg',
    name: 'Български',
    country: 'BG',
    locale: 'bg-BG',
    timezone: 'europe_sofia',
    currency: { symbol: 'лв.', code: 'BGN', key: 'main.bgCurrency' },
    flag: 'bg',
    dateFormat: 'DD.MM.YYYY',
  },
  it: {
    code: 'it',
    name: 'Italiano',
    country: 'IT',
    locale: 'it-IT',
    timezone: 'europe_rome',
    currency: { symbol: '€', code: 'EUR', key: 'main.euroCurrency' },
    flag: 'it',
    dateFormat: 'DD/MM/YYYY',
  },
};

// Legacy exports for backward compatibility
export const LANGUAGE_TO_COUNTRY_MAP: Record<SupportedLanguage, string> = {
  en: 'UK',
  bg: 'BG',
  it: 'IT',
};

export const LANGUAGE_TO_LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: 'en',
  bg: 'bg',
  it: 'it',
};

export const LANGUAGE_TO_TIMEZONE_MAP: Record<SupportedLanguage, string> = {
  en: 'europe_london',
  bg: 'europe_sofia',
  it: 'europe_rome',
};

// Helper functions
export const getLanguageConfig = (language: string): LanguageConfig => {
  return LANGUAGE_CONFIGS[language as SupportedLanguage] || LANGUAGE_CONFIGS.en;
};

export const getCountryForLanguage = (language: string): string => {
  return getLanguageConfig(language).country;
};

export const getLocaleForLanguage = (language: string): string => {
  return getLanguageConfig(language).locale.split('-')[0];
};

export const getTimezoneForLanguage = (language: string): string => {
  return getLanguageConfig(language).timezone;
};

export const isLanguageSupported = (language: string): language is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
};

export const formatCurrency = (amount: number, language: string, shopId?: number): string => {
  // Check if shopId is one of the Italian shops - prioritize this over language
  if (shopId && italianShopIds.includes(shopId)) {
    const value = amount / 100;
    if (value <= 0) {
      return `€0.00`;
    }
    return `€${value.toFixed(2)}`;
  }

  const config = getLanguageConfig(language);
  const value = amount / 100;

  if (value <= 0) {
    if (config.currency.key) {
      // For BG and IT, use translation key
      return config.currency.code === 'EUR' ? `€0.00` : `0.00 ${config.currency.symbol}`;
    }
    return `${config.currency.symbol}0.00`;
  }

  if (config.currency.code === 'EUR') {
    // For EUR, show symbol first
    return `€${value.toFixed(2)}`;
  } else if (config.currency.key) {
    // For BG, show number first then symbol
    return `${value.toFixed(2)} ${config.currency.symbol}`;
  }

  // For UK, show symbol first
  return `${config.currency.symbol}${value.toFixed(2)}`;
};

export const getCurrencyConfig = (language: string) => {
  const config = LANGUAGE_CONFIGS[language as SupportedLanguage];
  if (!config) {
    throw new Error(`Unsupported language: ${language}`);
  }
  return config.currency;
};
